import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { excelApi } from '../services/api';
import { useAuth } from './AuthContext';

const OrderImportContext = createContext(null);

// Request ownership follows the signed-in session, so navigation cannot recreate an in-flight submission.
const OrderImportSession = ({ children }) => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const submitting = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!uploading) return undefined;
    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [uploading]);
  const chooseFile = (next) => {
    if (submitting.current) return false;
    setResult(null);
    setError('');
    if (!next) {
      setFile(null);
      return false;
    }
    if (!/\.xlsx?$/i.test(next.name)) {
      setError('请选择 .xls 或 .xlsx 文件。CSV 请先用 Excel 另存为 .xlsx。');
      setFile(null);
      return false;
    }
    if (next.size >= 10 * 1024 * 1024) {
      setError('文件需小于 10 MB，请拆分后提交。');
      setFile(null);
      return false;
    }
    if (next.size === 0) {
      setError('文件为空，请检查内容后重新选择。');
      setFile(null);
      return false;
    }
    setFile(next);
    return false;
  };
  const submit = async () => {
    if (!file || submitting.current) return;
    submitting.current = true;
    setUploading(true);
    setError('');
    setResult(null);
    const data = new FormData();
    data.append('file', file);
    try {
      const response = await excelApi.parseExcel(data, user?.username);
      if (response.data?.code !== 200)
        throw new Error(response.data?.message || '文件处理失败');
      if (!mounted.current) return;
      setResult({
        ...response.data.data,
        fileName: file.name,
        returnedAt: new Date().toISOString(),
      });
      setFile(null);
    } catch (failure) {
      if (mounted.current)
        setError(
          (failure.message || '提交结果未确认') +
            '。请先查看订单，核对处理结果后再决定是否重新提交。',
        );
    } finally {
      submitting.current = false;
      if (mounted.current) setUploading(false);
    }
  };
  return (
    <OrderImportContext.Provider
      value={{ file, uploading, error, result, chooseFile, submit }}
    >
      {children}
    </OrderImportContext.Provider>
  );
};
export const OrderImportProvider = ({ children }) => {
  const { user } = useAuth();
  return (
    <OrderImportSession key={user?.username || 'anonymous'}>
      {children}
    </OrderImportSession>
  );
};
export const useOrderImport = () => useContext(OrderImportContext);
